import get from 'lodash/get';

import {
  CacheGroupedItemsCurrencies,
  CacheGroupedItemsGroupByEnum,
  FetchCacheGroupedItemsFilters,
  FetchCacheGroupedItemsSortEnum,
  FetchCacheGroupedItemsSortType,
  FetchGroupedItemsCacheKey,
  FetchGroupedItemsGqlQuery,
  FetchGroupedItemsLimit,
  FetchGroupedItemsSortType,
  GroupedItemsCurrencies,
  GroupedItemsCurrencyRateID,
  GroupedItemsGroupByEnum
} from '../../groupedItemsTypes';
import {
  FetchCacheItemSources,
  FetchCacheItemsSources,
  FetchCacheItemsTrackTotalHits,
  FetchCacheItemsTrackWithMetadata,
  FetchItemCacheKey,
  FetchItemGqlQuery,
  FetchItemsGqlQuery
} from '../../../../types';
import { PermissionAction } from '../../../permissions/permissionsTypes';
import { ReadInfiniteIndexQueryBaseNodeType } from '../../../common/reactQuery/hooks/useReadInfiniteIndexQuery';

import {
  CacheInfiniteIndexQueryOnSuccess,
  useCacheInfiniteIndexQuery
} from '../../../common/reactQuery/hooks/useCacheInfiniteIndexQuery';

import {
  GroupedItemsPermissions,
  INITIAL_CACHE_GROUPED_ITEMS_FILTERS,
  INITIAL_CACHE_GROUPED_ITEMS_LIMIT,
  INITIAL_CACHE_GROUPED_ITEMS_SORT
} from '../../groupedItemsConstants';

interface CacheProformaGroupedItemsWithPrefetchItemOptions {
  fetchItemCacheKey: FetchItemCacheKey;
  fetchItemQuery: FetchItemsGqlQuery;
  fetchCacheItemQuery?: FetchItemGqlQuery;
  fetchCacheItemSource?: FetchCacheItemSources;
}

interface CacheProformaGroupedItemsWithoutPrefetchItemOptions {
  fetchItemCacheKey?: never;
  fetchItemQuery?: never;
  fetchCacheItemQuery?: never;
  fetchCacheItemSource?: never;
}

type CacheProformaGroupedItemsAdditionalParams = {
  convertTo?: CacheGroupedItemsCurrencies | GroupedItemsCurrencies;
  currencyRateId?: GroupedItemsCurrencyRateID;
  groupBy?: CacheGroupedItemsGroupByEnum[] | GroupedItemsGroupByEnum[];
  groupItemsByType?: boolean;
  showTotal?: boolean;
  sort?: FetchCacheGroupedItemsSortEnum | FetchGroupedItemsSortType;
};

interface CacheProformaGroupedItemsDefaultOptions<
  NodeType,
  MetaType = unknown
> {
  action?: PermissionAction;
  additionalParams?: CacheProformaGroupedItemsAdditionalParams;
  cacheKey: FetchGroupedItemsCacheKey;
  cacheQuery?: FetchGroupedItemsGqlQuery;
  initialFilters?: FetchCacheGroupedItemsFilters;
  initialLimit?: FetchGroupedItemsLimit;
  initialSort?: FetchCacheGroupedItemsSortType | FetchGroupedItemsSortType;
  options?: {
    cacheTime?: number;
    staleTime?: number;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
    onSuccess?: CacheInfiniteIndexQueryOnSuccess<NodeType, MetaType>;
  };
  query: FetchGroupedItemsGqlQuery;
  source?: FetchCacheItemsSources;
  trackTotalHits?: FetchCacheItemsTrackTotalHits;
  withMetadata?: FetchCacheItemsTrackWithMetadata;
}

type CacheProformaGroupedItemsOptions<NodeType, MetaType> =
  CacheProformaGroupedItemsDefaultOptions<NodeType, MetaType> &
    (
      | CacheProformaGroupedItemsWithPrefetchItemOptions
      | CacheProformaGroupedItemsWithoutPrefetchItemOptions
    );

const scope = 'groupedItems';

function useCacheProformaGroupedItems<
  GroupedItemItemType extends ReadInfiniteIndexQueryBaseNodeType,
  GroupedItemMetaType = undefined
>({
  action,
  additionalParams,
  cacheKey,
  cacheQuery,
  fetchCacheItemQuery,
  fetchCacheItemSource = FetchCacheItemSources.MONGO,
  fetchItemCacheKey,
  fetchItemQuery,
  initialFilters = INITIAL_CACHE_GROUPED_ITEMS_FILTERS,
  initialLimit = INITIAL_CACHE_GROUPED_ITEMS_LIMIT,
  initialSort = INITIAL_CACHE_GROUPED_ITEMS_SORT,
  options = {},
  query,
  source = FetchCacheItemsSources.MONGO,
  trackTotalHits = false,
  withMetadata = false
}: CacheProformaGroupedItemsOptions<GroupedItemItemType, GroupedItemMetaType>) {
  const {
    data,
    items,
    itemsError,
    itemsTotalCount,
    isFetched,
    isLoading,
    isFetchingNextPage,
    isPlaceholderData,
    currentPage,
    currentLimit,
    currentFilters,
    currentSort,
    hasNextPage,
    updateItemCache,
    loadMoreItems,
    filterItems,
    changeItemsFilters,
    clearItemsFilters,
    clearItemsFiltersPersistInitial,
    sortItems,
    limitItems,
    meta,
    dataUpdatedAt
  } = useCacheInfiniteIndexQuery<GroupedItemItemType, GroupedItemMetaType>({
    action:
      action || GroupedItemsPermissions.READ_CACHE_PROFORMA_GROUPED_ITEMS_QUERY,
    additionalParams,
    cacheKey,
    cacheQuery,
    defaultApi: 'cache_api',
    fetchCacheItemQuery,
    fetchCacheItemSource,
    fetchItemCacheKey,
    fetchItemQuery,
    initialFilters,
    initialLimit,
    initialSort,
    options,
    query,
    scope,
    source,
    trackTotalHits,
    withMetadata
  });

  return {
    groupedItemsData: data,
    groupedItems: items?.map((item) => ({
      ...item,
      itemName: get(item, 'itemName') || get(item, 'itemTypeName')
    })),
    groupedItemsError: itemsError,
    groupedItemsTotalCount: itemsTotalCount,
    groupedItemsFetched: isFetched,
    groupedItemsLoading: isLoading,
    groupedItemsFetchingNextPage: isFetchingNextPage,
    groupedItemsIsPlaceholderData: isPlaceholderData,
    groupedItemsFilters: currentFilters,
    groupedItemsSort: currentSort,
    groupedItemsPage: currentPage,
    groupedItemsLimit: currentLimit,
    groupedItemsHasNextPage: hasNextPage,
    updateGroupedItemCache: updateItemCache,
    loadMoreGroupedItems: loadMoreItems,
    filterGroupedItems: filterItems,
    changeGroupedItemsFilters: changeItemsFilters,
    clearGroupedItemsFilters: clearItemsFilters,
    clearGroupedItemsFiltersPersistInitial: clearItemsFiltersPersistInitial,
    sortGroupedItems: sortItems,
    limitGroupedItems: limitItems,
    groupedItemsMeta: meta,
    groupedItemsDataUpdatedAt: dataUpdatedAt
  };
}

export default useCacheProformaGroupedItems;
